import React from "react";
import { useFormState } from "react-final-form";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types";
import { Box, Collapse, IconButton, List, ListItem, ListItemButton, ListItemText, Theme } from "@mui/material";
import { StyledCheckbox } from "@shared/form/CheckboxField";
import { Tooltip } from "@shared/Tooltip";

import { GroupWithChildren } from "./GroupFilterHeader";
import { getSubtreeSelectionState } from "./GroupFilterHeader.utils";

const useStyles = makeStyles()((theme: Theme) => ({
    expandIcon: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        marginRight: theme.spacing(1),
    },
    itemContent: {
        display: "flex",
        alignItems: "center",
        width: "100%",
    },
    listItemText: {
        margin: 0,
    },
}));

type GroupTreeItemProps = {
    group: GroupWithChildren;
    expandedGroups: number[];
    onExpand: (groupId: number) => void;
    depth?: number;
    onChange: (id: string, checked: boolean) => void;
    onToggleGroup?: (group: GroupWithChildren) => void;
    isDisabled?: (group: Group) => boolean;
    disabledTooltip?: string;
};

export const GroupFilterItem = ({ depth = 0, ...props }: GroupTreeItemProps) => {
    const { classes } = useStyles();
    const hasChildren = props.group.children.length > 0;

    const { values } = useFormState();
    const groupId = "$" + props.group.id;
    const checked = values?.[groupId];
    const expanded = props.expandedGroups.includes(props.group.id);
    const disabled = props.isDisabled?.(props.group) ?? false;

    const onToggle = () => {
        if (disabled) {
            return;
        }
        if (props.onToggleGroup) {
            props.onToggleGroup(props.group);
        } else {
            props.onChange(groupId, !checked);
        }
    };

    const subtreeState = hasChildren ? getSubtreeSelectionState(props.group, values) : null;
    const checkboxChecked = hasChildren ? subtreeState === "full" : Boolean(checked);
    const checkboxIndeterminate = Boolean(hasChildren && subtreeState === "partial");

    const onExpand: React.MouseEventHandler<HTMLSpanElement> = (e) => {
        e.stopPropagation();
        props.onExpand(props.group.id);
    };

    return (
        <React.Fragment>
            <ListItem style={{ paddingLeft: `${depth * 22}px` }} disablePadding>
                <ListItemButton onClick={onToggle} style={{ padding: 0 }}>
                    <Box className={classes.itemContent}>
                        <Box
                            component="span"
                            className={classes.expandIcon}
                            onClick={onExpand}
                            style={{ visibility: hasChildren ? "visible" : "hidden" }}
                        >
                            <IconButton size="large">
                                {expanded ? (
                                    <i className="fa-solid fa-chevron-down" style={{ fontSize: 16 }} />
                                ) : (
                                    <i className="fa-solid fa-chevron-right" style={{ fontSize: 16 }} />
                                )}
                            </IconButton>
                        </Box>

                        <Tooltip title={props.disabledTooltip ?? ""} hide={!disabled}>
                            <StyledCheckbox
                                checked={checkboxChecked}
                                indeterminate={checkboxIndeterminate}
                                disabled={disabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle();
                                }}
                            />
                        </Tooltip>
                        <ListItemText className={classes.listItemText} primary={props.group.name} />
                    </Box>
                </ListItemButton>
            </ListItem>
            {hasChildren ? (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {props.group.children.map((child) => (
                            <GroupFilterItem
                                key={child.id}
                                group={child}
                                depth={depth + 1}
                                expandedGroups={props.expandedGroups}
                                onExpand={props.onExpand}
                                onChange={props.onChange}
                                onToggleGroup={props.onToggleGroup}
                                isDisabled={props.isDisabled}
                                disabledTooltip={props.disabledTooltip}
                            />
                        ))}
                    </List>
                </Collapse>
            ) : null}
        </React.Fragment>
    );
};
